import DragAndDrop from '@components/system/DragAndDrop/DragAndDrop';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  CircularProgressProps,
  Grid,
  Typography,
  createStyles,
  makeStyles
} from '@material-ui/core';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  APP,
  APP_PRIMARY_COLOR,
  DEFAULT_RED_COLOR,
  DEFAULT_WHITE_COLOR,
  FONT_PRIMARY_COLOR,
  FONT_SECONDARY_COLOR
} from 'src/style/variable';
import {
  getInsuranceDetails,
  getInsuranceFile,
  updateInsuranceCards,
  uploadInsuranceCards
} from '@shared/services/AppointmentSchedulingService';
import {VerificationStatus} from '../ScheduleAppointment/ScheduleAppointment';
import Loader from '@components/framework/loader/Loader';
import {useDispatch, useSelector} from 'react-redux';
import {CancelOutlined} from '@material-ui/icons';
import NavRoutes from 'src/constants/route/NavRoutes';
import {useHistory, useLocation} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {State} from '@shared/store/reducers/Index';
import {_setAppointmentPortalInsuranceDetails} from '@shared/store/actions/AppointmentSchedularAction';
import {MAX_INSURANCE_IMAGE_SIZE} from 'src/constants/SystemConstants';
import {InsuranceCardDetails} from '@shared/services/service-mappers/AppointmentSchedulingMapper';
import AppointmentPortalPublicPageLayout from '../../components/AppointmentPortalPublicPageLayout/AppointmentPortalPublicPageLayout';
const UploadImageIcon = ({className = ''}: {className: string}) => {
  return (
    <svg
      focusable="false"
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={className}>
      {/* eslint-disable-next-line max-len */}
      <path d="M18 20H4V6h9V4H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-9h-2v9zm-7.79-3.17-1.96-2.36L5.5 18h11l-3.54-4.71zM20 4V1h-2v3h-3c.01.01 0 2 0 2h3v2.99c.01.01 2 0 2 0V6h3V4h-3z" />
    </svg>
  );
};

const uploadImageCardStyles: any = makeStyles(() =>
  createStyles({
    card: {},
    content: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem'
    },
    uploadIcon: {
      width: '10rem'
    },
    dragAndDropStyle: {
      display: 'block',
      padding: 0,
      height: 'auto'
    },
    imageContainer: {
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '20rem',
      alignItems: 'center'
    },
    displayedImage: {
      objectFit: 'cover',
      width: '100%',
      flex: 1,
      overflow: 'hidden'
    }
  })
);

const createURLofImgFile = (file: File | null) => {
  try {
    if (file) {
      const blob = new Blob([file]);
      return URL.createObjectURL(blob);
    }
    return '';
  } catch (err: any) {
    return '';
  }
};

function CircularProgressWithLabel(
  props: CircularProgressProps & {value: number}
) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Typography variant="caption" component="div" color="textSecondary">
          {Math.round(props.value)}%
        </Typography>
      </Box>
    </Box>
  );
}

const UploadImageCard = ({
  title,
  className = '',
  onFileSelected,
  defaultImgFile = null
}: {
  title: string;
  className?: string;
  onFileSelected: (file: any) => void;
  defaultImgFile?: File | null;
}): JSX.Element => {
  const classes = uploadImageCardStyles();
  const previewImgSrc = createURLofImgFile(defaultImgFile);

  const handleFileChange = (files: any): void => {
    if (files && files.length > 0) {
      const [file] = files;
      onFileSelected(file);
    } else {
      onFileSelected(null);
    }
  };
  return !defaultImgFile ? (
    <DragAndDrop
      title={
        <Card className={`${classes.card} ${className}`} elevation={4}>
          <CardContent className={classes.content}>
            <UploadImageIcon className={classes.uploadIcon} />
            <Typography variant="h2" component={'p'}>
              {title}
            </Typography>
          </CardContent>
        </Card>
      }
      className={classes.dragAndDropStyle}
      multiple={false}
      showSelectedFile
      handleFileChange={handleFileChange}
      acceptFileTypes={'image/*'}
    />
  ) : (
    <div className={classes.imageContainer}>
      <img src={previewImgSrc} className={classes.displayedImage} />
    </div>
  );
};

const uploadInsuranceImagesStyles: any = makeStyles(theme =>
  createStyles({
    uploadInsuranceImagesWrapper: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      padding: '1rem',
      gap: '2rem',
      background: DEFAULT_WHITE_COLOR,
      overflow: 'auto',
      overflowX: 'hidden'
    },
    uploadImagesCardWrapper: {
      maxWidth: '25rem',
      overflow: 'hidden',
      minHeight: '20rem',
      alignItems: 'center',
      [theme.breakpoints.up('md')]: {
        maxWidth: '800px'
      }
    },
    uploadCardItemWrapper: {
      maxWidth: `30rem`
    },
    title: {
      textAlign: 'center',
      fontSize: '2.5rem',
      color: FONT_PRIMARY_COLOR,
      maxWidth: '32ch'
    },
    header: {
      textAlign: 'center',
      fontSize: '1.5rem',
      color: FONT_PRIMARY_COLOR,
      maxWidth: '35ch',
      fontWeight: 'bold'
    },
    imageTypeAndSizeInfoWrapper: {
      textAlign: 'center',
      fontSize: '1.3rem',
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '1rem',
      paddingBlock: '1rem'
    },
    imageTypesAllowed: {
      color: FONT_PRIMARY_COLOR
    },
    imageSizeLimit: {
      color: FONT_SECONDARY_COLOR
    },
    verifyButton: {
      fontSize: '1.5rem',
      paddingInline: '2rem'
    },
    notVerifiedActionButtonsWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem',
      flexWrap: 'wrap'
    },
    resetButton: {
      fontSize: '1.5rem',
      paddingInline: '2rem'
    },
    insuranceVerifiedInfoWrapper: {
      fontSize: '2rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '0.5rem'
    },
    verifyingInsuranceTextWrapper: {
      fontSize: '2rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem'
    },
    checkedIcon: {
      fontSize: '3rem',
      color: APP.STATUS.SUCCESS
    },
    verifiedText: {
      color: APP_PRIMARY_COLOR
    },
    removeFileButtonWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem'
    },
    removeFilesButton: {
      color: APP_PRIMARY_COLOR,
      fontSize: '1.2rem',
      '& .MuiButton-label': {
        fontWeight: 'normal'
      }
    },
    needExtraInfo: {
      marginTop: '0.5rem',
      fontSize: '1.1rem'
    },
    imageSizeError: {
      color: DEFAULT_RED_COLOR,
      marginTop: '0.5rem'
    },
    InsuranceParentWrapper: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column'
    }
  })
);

const allowedImageTypes = ['JPEG', 'JPG', 'PNG'];

const UploadInsuranceWrapper = (): JSX.Element => {
  const {insuranceCardDetails} = useSelector(
    (state: State) => state.appointmentPortal
  );
  const location = useLocation();
  const isEdit = new URLSearchParams(location.search).get('edit');

  const [uploadInsuranceState, setUploadInsuranceState] = useState({
    verificationStatus: 'not-verified' as VerificationStatus,
    verificationProgressValue: 0,
    frontImg: null as File | null,
    backImg: null as File | null,
    loadingInsuranceInformation: isEdit === 'true',
    existingFrontImg: null as File | null,
    existingBackImg: null as File | null
  });

  const resetChanges = () => {
    setUploadInsuranceState(p => ({
      verificationStatus: insuranceCardDetails?.id
        ? 'verified'
        : 'not-verified',
      verificationProgressValue: 0,
      frontImg: p.existingFrontImg,
      backImg: p.existingBackImg,
      loadingInsuranceInformation: false,
      existingFrontImg: p.existingFrontImg,
      existingBackImg: p.existingBackImg
    }));
  };

  const updateProgressValueBy = (val: number, maxValue = 100) => {
    setUploadInsuranceState(p => ({
      ...p,
      verificationProgressValue:
        p.verificationProgressValue + val >= maxValue
          ? maxValue
          : p.verificationProgressValue + val
    }));
  };

  useEffect(() => {
    const getInsuranceImages = async (
      frontImgId: string,
      backImgId: string
    ) => {
      setUploadInsuranceState(p => ({
        ...p,
        loadingInsuranceInformation: true
      }));
      try {
        const resFrontImg = await getInsuranceFile(frontImgId);
        const resBackImg = await getInsuranceFile(backImgId);
        setUploadInsuranceState(p => ({
          ...p,
          frontImg: resFrontImg,
          backImg: resBackImg,
          existingFrontImg: resFrontImg,
          existingBackImg: resBackImg,
          verificationStatus: 'verified',
          verificationProgressValue: 0
        }));
      } catch (err: any) {
        /**/
      } finally {
        setUploadInsuranceState(p => ({
          ...p,
          loadingInsuranceInformation: false
        }));
      }
    };
    if (
      isEdit === 'true' &&
      insuranceCardDetails?.file_id1 &&
      insuranceCardDetails?.file_id2
    ) {
      getInsuranceImages(
        insuranceCardDetails.file_id1,
        insuranceCardDetails.file_id2
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceCardDetails?.file_id1, insuranceCardDetails?.file_id2]);

  const updateVerificationStatus = (verificationStatus: VerificationStatus) => {
    setUploadInsuranceState(p => ({
      ...p,
      verificationStatus,
      verificationProgressValue: 0
    }));
  };

  const handleFileChange = (fileType: 'front' | 'back', file: null | File) => {
    // change the verification status if a new file is uploaded or existing file is removed
    if (fileType === 'front') {
      setUploadInsuranceState(p => ({
        ...p,
        frontImg: file,
        verificationStatus: 'not-verified'
      }));
    } else {
      setUploadInsuranceState(p => ({
        ...p,
        backImg: file,
        verificationStatus: 'not-verified'
      }));
    }
  };

  return (
    <AppointmentPortalPublicPageLayout
      className="payment-login-page appointment-scheduler-upload-insurance-page"
      testId="pcl-appointmentPortalUploadInsurancePage">
      <UploadOrEditInsurance
        {...uploadInsuranceState}
        updateFileChange={handleFileChange}
        updateVerificationStatus={updateVerificationStatus}
        editInsurance={isEdit === 'true'}
        showGoToHome
        updateProgressValueBy={updateProgressValueBy}
        resetChanges={
          isEdit === 'true'
            ? resetChanges
            : () => {
                /**/
              }
        }
      />
    </AppointmentPortalPublicPageLayout>
  );
};
export {UploadInsuranceWrapper};

const UploadOrEditInsurance = ({
  className = '',
  editInsurance = false,
  frontImg,
  backImg,
  verificationProgressValue,
  verificationStatus = 'not-verified',
  updateVerificationStatus,
  updateFileChange,
  loadingInsuranceInformation = false,
  resetChanges,
  showGoToHome = false,
  updateProgressValueBy
}: {
  className?: string;
  editInsurance?: boolean;
  verificationStatus: VerificationStatus;
  verificationProgressValue: number;
  frontImg: File | null;
  backImg: File | null;
  loadingInsuranceInformation: boolean;
  updateVerificationStatus: (status: VerificationStatus) => void;
  updateFileChange: (fileType: 'front' | 'back', file: null | File) => void;
  resetChanges?: () => void;
  showGoToHome?: boolean;
  updateProgressValueBy?: (val: number, maxValue?: number) => void;
}): JSX.Element => {
  const {t} = useTranslation();

  const {insuranceCardDetails, patientAppointmentToken, details} = useSelector(
    (state: State) => state.appointmentPortal
  );

  const {enqueueSnackbar} = useSnackbar();

  const setVerifiedTimer = useRef<any>();
  const verificationProgressTimer = useRef<any>();
  const clearTimerIntervals = () => {
    if (setVerifiedTimer.current) {
      clearTimeout(setVerifiedTimer.current);
      setVerifiedTimer.current = null;
    }
    if (verificationProgressTimer.current) {
      clearInterval(verificationProgressTimer.current);
      verificationProgressTimer.current = null;
    }
  };
  useEffect(() => {
    if (verificationStatus !== 'verifying') {
      clearTimerIntervals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationStatus]);

  useEffect(() => {
    return () => {
      // clear the interval when component unmounts
      clearTimerIntervals();
    };
  }, []);

  const dispatch = useDispatch();
  const location = useLocation<{fromPaymentPortal?: boolean}>();
  const hasComeFromPaymentPortal = location.state?.fromPaymentPortal;
  const {clinicIdForAppointmentPortal} = useSelector(
    (state: State) => state.payments
  );

  const handleVerifyClick = async () => {
    if (!frontImg || !backImg) {
      enqueueSnackbar(
        t(
          'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.PLEASE_ADD_BOTH_FRONT_AND_BACK_IMAGES'
        ),
        SnackBarConfig('e')
      );
      return;
    }
    updateVerificationStatus('verifying');
    verificationProgressTimer.current = setInterval(() => {
      if (updateProgressValueBy) {
        updateProgressValueBy(25, 75);
      }
    }, 250);
    try {
      const data = new FormData();
      data.append('insurance_file1', frontImg);
      data.append('insurance_file2', backImg);
      data.append('patient_id', `${details?.logged_in_patient?.id}`);
      let clinicId = details?.clinic.id as number;
      if (hasComeFromPaymentPortal && clinicIdForAppointmentPortal) {
        clinicId = clinicIdForAppointmentPortal;
      }
      data.append('clinic_id', `${clinicId}`);
      if (!editInsurance) {
        await uploadInsuranceCards(data);
      } else {
        await updateInsuranceCards(
          (insuranceCardDetails as InsuranceCardDetails)?.id,
          data
        );
      }
      updateVerificationStatus('verified');

      // after successfully uploading/updating insurance images update the insurance details
      const insuranceRes = await getInsuranceDetails();
      let loggedInPatientInsurance: InsuranceCardDetails | undefined;
      if (
        Array.isArray(insuranceRes.data.insurance) &&
        details?.logged_in_patient?.id
      ) {
        loggedInPatientInsurance = insuranceRes.data.insurance.find(
          ins => ins.patient_id === details.logged_in_patient.id
        );
      }
      if (loggedInPatientInsurance) {
        dispatch(
          _setAppointmentPortalInsuranceDetails(loggedInPatientInsurance)
        );
      }
    } catch (error: any) {
      updateVerificationStatus('not-verified');
      enqueueSnackbar(
        error?.message
          ? error.message
          : t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.ERROR_VERIFYING_INSURANCE'
            ),
        SnackBarConfig('e')
      );
    }
  };

  const history = useHistory();
  const classes = uploadInsuranceImagesStyles();

  const frontImgBlob = frontImg ? new Blob([frontImg]) : null;
  const backImgBlob = backImg ? new Blob([backImg]) : null;

  const frontImgSizeLimitExceed = frontImgBlob
    ? frontImgBlob.size > MAX_INSURANCE_IMAGE_SIZE.bytes
    : false;
  const backImgSizeLimitExceed = backImgBlob
    ? backImgBlob.size > MAX_INSURANCE_IMAGE_SIZE.bytes
    : false;

  const doesImgSizeExceedLimit =
    frontImgSizeLimitExceed || backImgSizeLimitExceed;

  const showRemoveFilesButton = editInsurance
    ? !!frontImg || !!backImg
    : verificationStatus === 'not-verified' && (!!frontImg || !!backImg);

  return (
    <Box className={`${classes.uploadInsuranceImagesWrapper} ${className}`}>
      {loadingInsuranceInformation && <Loader />}
      {!loadingInsuranceInformation && (
        <>
          <h1 className={classes.title}>
            {t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.ADD_INSURANCE_CARD'
            )}
          </h1>
          <p className={classes.header}>
            {editInsurance
              ? t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.UPDATE_IMAGES_OF_INSURANCE_CARD'
                )
              : t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.UPLOAD_IMAGES_OF_INSURANCE_CARD'
                )}
          </p>
          <Grid
            container
            spacing={4}
            className={classes.uploadImagesCardWrapper}>
            <Grid item md={6} xs={12}>
              <UploadImageCard
                title={t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.UPLOAD_FRONT_OF_INSURANCE_CARD'
                )}
                onFileSelected={file => updateFileChange('front', file)}
                defaultImgFile={frontImg}
              />
              {verificationStatus === 'not-verified' &&
                frontImgSizeLimitExceed && (
                  <p className={classes.imageSizeError}>
                    {t(
                      'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.IMAGE_SIZE_LIMIT_EXCEED',
                      {maxFileSize: MAX_INSURANCE_IMAGE_SIZE.formattedSize}
                    )}
                  </p>
                )}
            </Grid>
            <Grid item md={6} xs={12}>
              <UploadImageCard
                title={t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.UPLOAD_BACK_OF_INSURANCE_CARD'
                )}
                onFileSelected={file => updateFileChange('back', file)}
                defaultImgFile={backImg}
              />
              {verificationStatus === 'not-verified' &&
                backImgSizeLimitExceed && (
                  <p className={classes.imageSizeError}>
                    {t(
                      'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.IMAGE_SIZE_LIMIT_EXCEED',
                      {maxFileSize: MAX_INSURANCE_IMAGE_SIZE.formattedSize}
                    )}
                  </p>
                )}
            </Grid>
          </Grid>
          {showRemoveFilesButton && (
            <div className={classes.removeFileButtonWrapper}>
              <Button
                onClick={() => {
                  updateFileChange('front', null);
                  updateFileChange('back', null);
                }}
                endIcon={<CancelOutlined />}
                className={classes.removeFilesButton}>
                {t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.REMOVE_FILES'
                )}
              </Button>
            </div>
          )}
          <div className={classes.imageTypeAndSizeInfoWrapper}>
            <p className={classes.imageTypesAllowed}>
              {t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.ALLOWED_FILE_TYPES',
                {types: allowedImageTypes.join(`, `)}
              )}
            </p>
            <p className={classes.imageSizeLimit}>
              {t(
                'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.MAX_FILE_SIZE',
                {maxFileSize: MAX_INSURANCE_IMAGE_SIZE.formattedSize}
              )}
            </p>
          </div>
          {verificationStatus === 'not-verified' && (
            <div className={classes.notVerifiedActionButtonsWrapper}>
              <Button
                className={classes.verifyButton}
                variant="contained"
                color="primary"
                disabled={!frontImg || !backImg || doesImgSizeExceedLimit}
                onClick={handleVerifyClick}>
                {!editInsurance
                  ? t(
                      'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.VERIFY_INSURANCE'
                    )
                  : t(
                      'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.UPDATE_AND_VERIFY_INSURANCE'
                    )}
              </Button>
              {editInsurance && verificationStatus === 'not-verified' && (
                <Button
                  className={classes.verifyButton}
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    if (resetChanges) {
                      resetChanges();
                    }
                  }}>
                  {t(
                    'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.RESET_CHANGES'
                  )}
                </Button>
              )}
            </div>
          )}
          {verificationStatus === 'verified' && (
            <div className={classes.InsuranceParentWrapper}>
              <p className={classes.insuranceVerifiedInfoWrapper}>
                <CheckCircleOutlineOutlinedIcon
                  className={classes.checkedIcon}
                />
                <span className={classes.verifiedText}>
                  {t(
                    'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.INSURANCE_VERIFICATION_PENDING'
                  )}
                </span>
              </p>
              <p className={classes.needExtraInfo}>
                {t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.NEED_EXTRA_INFO'
                )}
              </p>
            </div>
          )}
          {verificationStatus === 'verifying' && (
            <p className={classes.verifyingInsuranceTextWrapper}>
              <CircularProgressWithLabel value={verificationProgressValue} />
              <span>
                {t(
                  'APPOINTMENTS_SCHEDULAR_PORTAL.UPLOAD_INSURANCE_CARD.VERIFYING_INSURANCE_ELIGIBILITY'
                )}
              </span>
            </p>
          )}
          {showGoToHome && (
            <Button
              className={classes.verifyButton}
              variant="contained"
              disabled={verificationStatus !== 'verified'}
              color="primary"
              onClick={() => {
                history.push(
                  `/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.BASE}/${patientAppointmentToken}/${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.HOME}`
                );
              }}>
              {t('APPOINTMENTS_SCHEDULAR_PORTAL.GO_HOME')}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default UploadOrEditInsurance;
